import {
  clearMapAssets,
  clearMetadata,
  deleteMapAssetsById,
  findMapAssetsByGeoHash,
  findMapAssetsById,
  getAllMapAssetIds,
  getAllMapAssets,
  getMapAssetIdsByAssetTypes,
  getMetadata,
  saveMapAssets,
  setMetadata
} from '~/database/db'
import type { MapAssetObject } from '~/types/map-assets.types'
import type { AssetType } from '~/types/asset'

export interface HashCacheStatus {
  cached: string[]
  stale: string[]
  uncached: string[]
  excluded: string[]
}

export interface AssetCacheComposable {
  isCacheValid: () => Promise<boolean>
  getCachedHashes: () => Promise<string[]>
  isHashInCache: (hash: string, minPrecision?: number) => Promise<boolean>
  checkHashesCacheStatus: (hashes: string[], minPrecision?: number) => Promise<HashCacheStatus>
  addCachedHashes: (hashes: string[]) => Promise<void>
  updateLastFetchTime: () => Promise<void>
  getCachedAssets: (hashes: string[]) => Promise<MapAssetObject[]>
  getAllCachedAssets: () => Promise<MapAssetObject[]>
  getAssetById: (id: number) => Promise<MapAssetObject | null>
  getAssetsByIds: (ids: number[]) => Promise<MapAssetObject[]>
  getAllCachedAssetIds: () => Promise<number[]>
  getCachedAssetIdsByTypes: (assetTypeStringIds: string[], availableAssetTypes: AssetType[]) => Promise<number[]>
  cacheAssets: (assets: MapAssetObject[]) => Promise<void>
  deleteAssetsByIds: (ids: number[]) => Promise<void>
  setFilteredIds: (ids: number[]) => Promise<void>
  getFilteredIds: () => Promise<number[] | undefined>
  clearFilteredIds: () => Promise<void>
  setCurrentLicenseId: (licenseId: number) => Promise<void>
  getCurrentLicenseId: () => Promise<number | null>
  isCacheValidForLicense: (licenseId: number) => Promise<boolean>
  clearAll: () => Promise<void>
}

const MAP_CACHE_TTL_IN_SECONDS = 300
const REGION_REFRESH_INTERVAL_SECONDS = 120

type CachedRegionsMap = Record<string, number>

const ensureRegionsMap = (input: unknown): CachedRegionsMap => {
  if (!input) {
    return {}
  }

  if (Array.isArray(input)) {
    return input.reduce<CachedRegionsMap>((acc, hash) => {
      if (typeof hash === 'string') {
        acc[hash] = 0
      }
      return acc
    }, {})
  }

  if (typeof input === 'object' && input !== null) {
    const result: CachedRegionsMap = {}
    for (const [key, value] of Object.entries(input)) {
      if (typeof key === 'string') {
        const numericValue = typeof value === 'number' ? value : Number(value) || 0
        result[key] = numericValue
      }
    }
    return result
  }

  return {}
}

const readCachedRegions = async (): Promise<CachedRegionsMap> => {
  const fetchedRegionString = await getMetadata('fetchedRegions')
  if (!fetchedRegionString) {
    return {}
  }

  try {
    return ensureRegionsMap(JSON.parse(fetchedRegionString))
  } catch {
    return {}
  }
}

const writeCachedRegions = async (regions: CachedRegionsMap): Promise<void> => {
  await setMetadata('fetchedRegions', JSON.stringify(regions))
}

const findBestCachedMatch = (hash: string, cachedRegions: CachedRegionsMap, minPrecision: number): string | null => {
  let bestMatch: string | null = null

  for (const cachedHash of Object.keys(cachedRegions)) {
    if (!hash.startsWith(cachedHash)) {
      continue
    }

    if (cachedHash.length < minPrecision) {
      continue
    }

    if (!bestMatch || cachedHash.length > bestMatch.length) {
      bestMatch = cachedHash
    }
  }

  return bestMatch
}

export const useAssetCache = (): AssetCacheComposable => {
  const isCacheValid = async (): Promise<boolean> => {
    const lastFetch = await getMetadata('lastFetchTime')
    if (lastFetch) {
      const diff = Date.now() - Number(lastFetch)
      return diff / 1000 < MAP_CACHE_TTL_IN_SECONDS
    }

    return false
  }

  const getCachedHashes = async (): Promise<string[]> => {
    const regions = await readCachedRegions()
    return Object.keys(regions)
  }

  const isHashInCache = async (hash: string, minPrecision = 1): Promise<boolean> => {
    const regions = await readCachedRegions()
    const bestMatch = findBestCachedMatch(hash, regions, minPrecision)
    if (!bestMatch) {
      return false
    }

    const timestamp = regions[bestMatch]
    const ageSeconds = (Date.now() - timestamp) / 1000

    return ageSeconds <= REGION_REFRESH_INTERVAL_SECONDS
  }

  const checkHashesCacheStatus = async (hashes: string[], minPrecision = 1): Promise<HashCacheStatus> => {
    const regions = await readCachedRegions()
    const now = Date.now()

    const cached: string[] = []
    const stale: string[] = []
    const uncached: string[] = []

    for (const hash of hashes) {
      const bestMatch = findBestCachedMatch(hash, regions, minPrecision)

      if (!bestMatch) {
        uncached.push(hash)
        continue
      }

      const timestamp = regions[bestMatch] ?? 0
      const ageSeconds = (now - timestamp) / 1000

      if (timestamp === 0 || ageSeconds > REGION_REFRESH_INTERVAL_SECONDS) {
        stale.push(hash)
      } else {
        cached.push(hash)
      }
    }

    const excluded: string[] = []
    const hashesToFetch = new Set([...uncached, ...stale])

    for (const cachedHash of Object.keys(regions)) {
      if ([...hashesToFetch].some((hash) => cachedHash.startsWith(hash))) {
        excluded.push(cachedHash)
      }
    }

    return {
      cached,
      stale,
      uncached,
      excluded
    }
  }

  const addCachedHashes = async (hashes: string[]): Promise<void> => {
    if (!hashes.length) {
      return
    }

    const regions = await readCachedRegions()
    const now = Date.now()

    for (const hash of hashes) {
      regions[hash] = now
    }

    await writeCachedRegions(regions)
  }

  const updateLastFetchTime = async (): Promise<void> => {
    await setMetadata('lastFetchTime', String(Date.now()))
  }

  const getCachedAssets = async (hashes: string[]): Promise<MapAssetObject[]> => {
    const uniqueHashes = Array.from(new Set(hashes))
    if (!uniqueHashes.length) {
      return []
    }

    const results = await Promise.all(uniqueHashes.map((geohash) => findMapAssetsByGeoHash(geohash)))

    // Deduplicate assets by ID to prevent duplicates when geohashes overlap
    const assetMap = new Map<number, MapAssetObject>()
    for (const result of results) {
      for (const asset of result) {
        assetMap.set(asset.id, asset)
      }
    }

    return Array.from(assetMap.values())
  }

  const getAllCachedAssets = async (): Promise<MapAssetObject[]> => {
    return await getAllMapAssets()
  }

  const getAssetById = async (id: number): Promise<MapAssetObject | null> => {
    const [asset] = await findMapAssetsById([id])
    return asset || null
  }

  const getAssetsByIds = async (ids: number[]): Promise<MapAssetObject[]> => {
    const assets = await findMapAssetsById(ids)
    return assets.filter((asset) => asset !== undefined) as MapAssetObject[]
  }

  const getAllCachedAssetIds = async (): Promise<number[]> => {
    return await getAllMapAssetIds()
  }

  const getCachedAssetIdsByTypes = async (
    assetTypeStringIds: string[],
    availableAssetTypes: AssetType[]
  ): Promise<number[]> => {
    const numericIds = assetTypeStringIds
      .map((stringId) => {
        const assetType = availableAssetTypes.find((type) => type.assetTypeId === stringId)
        if (!assetType) {
          console.warn('Asset type not found for string ID:', stringId)
          return null
        }
        return assetType.id
      })
      .filter((id): id is number => id !== null)

    if (numericIds.length === 0) {
      console.warn('No valid numeric asset type IDs found for:', assetTypeStringIds)
      return []
    }

    return await getMapAssetIdsByAssetTypes(numericIds)
  }

  const cacheAssets = async (assets: MapAssetObject[]): Promise<void> => {
    await saveMapAssets(assets)
  }

  const deleteAssetsByIds = async (ids: number[]): Promise<void> => {
    if (!ids.length) {
      return
    }

    await deleteMapAssetsById(ids)
  }

  const setFilteredIds = async (ids: number[]): Promise<void> => {
    await setMetadata('filteredIds', JSON.stringify(ids))
  }

  const getFilteredIds = async (): Promise<number[] | undefined> => {
    const filteredIdsJson = await getMetadata('filteredIds')
    return filteredIdsJson ? JSON.parse(filteredIdsJson) : undefined
  }

  const clearFilteredIds = async (): Promise<void> => {
    await setMetadata('filteredIds', '')
  }

  const setCurrentLicenseId = async (licenseId: number): Promise<void> => {
    await setMetadata('currentLicenseId', String(licenseId))
  }

  const getCurrentLicenseId = async (): Promise<number | null> => {
    const licenseId = await getMetadata('currentLicenseId')
    return licenseId ? Number(licenseId) : null
  }

  const isCacheValidForLicense = async (licenseId: number): Promise<boolean> => {
    const cachedLicenseId = await getCurrentLicenseId()
    return cachedLicenseId === null || cachedLicenseId === licenseId
  }

  const clearAll = async (): Promise<void> => {
    await Promise.all([clearMapAssets(), clearMetadata()])
  }

  return {
    isCacheValid,
    getCachedHashes,
    isHashInCache,
    checkHashesCacheStatus,
    addCachedHashes,
    updateLastFetchTime,
    getCachedAssets,
    getAllCachedAssets,
    getAssetById,
    getAssetsByIds,
    getAllCachedAssetIds,
    getCachedAssetIdsByTypes,
    cacheAssets,
    deleteAssetsByIds,
    setFilteredIds,
    getFilteredIds,
    clearFilteredIds,
    setCurrentLicenseId,
    getCurrentLicenseId,
    isCacheValidForLicense,
    clearAll
  }
}
